import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Eye, MousePointerClick, TrendingUp, ChevronLeft, ChevronRight, User, Users } from 'lucide-react';
import { apiClient } from '@/providers/api-client';
import { type Banner, calculateCTR } from './types';

interface EventUser {
  id: number;
  firstName: string | null;
  lastName: string | null;
  username: string | null;
  photoUrl: string | null;
}

interface BannerEventRecord {
  id: number;
  bannerId: number;
  userId: number | null;
  createdAt: string;
  user: EventUser | null;
}

export type EventView = 'views' | 'clicks' | 'unique-views' | 'unique-clicks';

interface BannerEventsDialogProps {
  banner: Banner | null;
  type: EventView | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const PAGE_SIZE = 20;

// path-сегмент эндпоинта и человекочитаемый заголовок по выбранной карточке
const VIEW_CONFIG: Record<EventView, { path: string; title: string; empty: string }> = {
  'views': { path: 'views', title: 'Просмотры', empty: 'Нет просмотров' },
  'clicks': { path: 'clicks', title: 'Клики', empty: 'Нет кликов' },
  'unique-views': { path: 'unique-views', title: 'Уникальные просмотры', empty: 'Нет просмотров' },
  'unique-clicks': { path: 'unique-clicks', title: 'Уникальные клики', empty: 'Нет кликов' },
};

function formatNumber(num: number): string {
  if (num >= 1000000) {
    return (num / 1000000).toFixed(1) + 'M';
  }
  if (num >= 1000) {
    return (num / 1000).toFixed(1) + 'K';
  }
  return num.toString();
}

export function BannerEventsDialog({ banner, type, open, onOpenChange }: BannerEventsDialogProps) {
  const [page, setPage] = useState(1);
  const [view, setView] = useState<EventView>(type ?? 'views');

  const bannerId = banner?.id ?? null;

  // При открытии/смене типа из таблицы — синхронизируем активную карточку
  useEffect(() => {
    if (open && type) {
      setView(type);
      setPage(1);
    }
  }, [open, type]);

  const selectView = (next: EventView) => {
    setView(next);
    setPage(1);
  };

  const config = VIEW_CONFIG[view];

  const { data, isLoading } = useQuery({
    queryKey: ['banners', bannerId, view, page],
    queryFn: () => apiClient.getWithMeta<BannerEventRecord[]>(
      `/banners/${bannerId}/${config.path}?page=${page}&limit=${PAGE_SIZE}`,
    ),
    enabled: open && bannerId !== null,
  });

  const records = data?.data ?? [];
  const viewsCount = banner?.viewsCount ?? 0;
  const clicksCount = banner?.clicksCount ?? 0;
  const uniqueViewsCount = banner?.uniqueViewsCount ?? 0;
  const uniqueClicksCount = banner?.uniqueClicksCount ?? 0;
  const ctr = banner ? calculateCTR(banner) : 0;

  const totalByView: Record<EventView, number> = {
    'views': viewsCount,
    'clicks': clicksCount,
    'unique-views': uniqueViewsCount,
    'unique-clicks': uniqueClicksCount,
  };
  const total = data?.meta?.total ?? totalByView[view];
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleString('ru-RU', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getUserDisplayName = (record: BannerEventRecord): string => {
    if (!record.user) {
      return 'Аноним';
    }
    const { firstName, lastName, username } = record.user;
    const fullName = [firstName, lastName].filter(Boolean).join(' ');
    if (fullName) {
      return fullName;
    }
    if (username) {
      return `@${username}`;
    }
    return `User #${record.user.id}`;
  };

  const Icon = view === 'views' || view === 'unique-views' ? Eye : MousePointerClick;

  const cardClass = (active: boolean): string =>
    `rounded-lg border p-3 text-center cursor-pointer transition-colors hover:border-primary/40 ${active ? 'bg-primary/5 border-primary/20' : ''}`;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[80vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Icon className="h-5 w-5" />
            {config.title} баннера #{bannerId}
            <Badge variant="secondary">{total}</Badge>
          </DialogTitle>
        </DialogHeader>

        {/* Сводка-фильтр: клик по карточке переключает список снизу */}
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          <button type="button" className={cardClass(view === 'views')} onClick={() => selectView('views')}>
            <div className="flex items-center justify-center gap-1 text-muted-foreground mb-1">
              <Eye className="h-3.5 w-3.5" />
              <span className="text-xs">Просмотры</span>
            </div>
            <p className="text-lg font-semibold">{formatNumber(viewsCount)}</p>
          </button>
          <button type="button" className={cardClass(view === 'clicks')} onClick={() => selectView('clicks')}>
            <div className="flex items-center justify-center gap-1 text-muted-foreground mb-1">
              <MousePointerClick className="h-3.5 w-3.5" />
              <span className="text-xs">Клики</span>
            </div>
            <p className="text-lg font-semibold">{formatNumber(clicksCount)}</p>
          </button>
          <button type="button" className={cardClass(view === 'unique-views')} onClick={() => selectView('unique-views')}>
            <div className="flex items-center justify-center gap-1 text-muted-foreground mb-1">
              <Users className="h-3.5 w-3.5" />
              <span className="text-xs">Уникальные просмотры</span>
            </div>
            <p className="text-lg font-semibold">{formatNumber(uniqueViewsCount)}</p>
          </button>
          <button type="button" className={cardClass(view === 'unique-clicks')} onClick={() => selectView('unique-clicks')}>
            <div className="flex items-center justify-center gap-1 text-muted-foreground mb-1">
              <Users className="h-3.5 w-3.5" />
              <span className="text-xs">Уникальные клики</span>
            </div>
            <p className="text-lg font-semibold">{formatNumber(uniqueClicksCount)}</p>
          </button>
          <div className="rounded-lg border p-3 text-center">
            <div className="flex items-center justify-center gap-1 text-muted-foreground mb-1">
              <TrendingUp className="h-3.5 w-3.5" />
              <span className="text-xs">CTR</span>
            </div>
            <p className="text-lg font-semibold">{ctr.toFixed(1)}%</p>
          </div>
        </div>

        {/* Список событий */}
        <div className="flex-1 overflow-y-auto min-h-0">
          {isLoading ? (
            <div className="flex items-center justify-center py-8 text-muted-foreground">Загрузка...</div>
          ) : records.length === 0 ? (
            <div className="flex items-center justify-center py-8 text-muted-foreground">
              {config.empty}
            </div>
          ) : (
            <div className="divide-y">
              {records.map((record) => (
                <div key={record.id} className="flex items-center gap-3 py-2.5 px-1">
                  {record.user?.photoUrl ? (
                    <img src={record.user.photoUrl} alt="" className="h-8 w-8 rounded-full object-cover flex-shrink-0" />
                  ) : (
                    <div className="h-8 w-8 rounded-full bg-muted flex items-center justify-center flex-shrink-0">
                      <User className="h-4 w-4 text-muted-foreground" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{getUserDisplayName(record)}</p>
                    {record.user?.username && record.user.firstName && (
                      <p className="text-xs text-muted-foreground">@{record.user.username}</p>
                    )}
                  </div>
                  <span className="text-xs text-muted-foreground whitespace-nowrap">
                    {formatDate(record.createdAt)}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>

        {totalPages > 1 && (
          <div className="flex items-center justify-between pt-2 border-t">
            <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span className="text-sm text-muted-foreground">{page} / {totalPages}</span>
            <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
